import { DiarizationType, WpWordsType } from '@app/modules/search/types';
import { toDateTimeStr, toTimestamp } from '@mi-front/core';
import { Row } from '@tanstack/react-table';
import { theme } from '@ui/styles';
import { lighten } from 'polished';
import { FormattedMessage } from 'react-intl';
import {
  FormattedFragmentsTranscriptionType,
  FormattedTranscriptionType,
  TranscriptionFormattedType,
  TranscriptionResponse,
  TranscriptionType,
} from './transcriptions.types';

export const pastelColors = [
  '#FF9AA2', // pastel pink
  '#7EAAC7', // pastel blue
  '#66E2D3', // pastel green
  '#D8B384', // pastel beige
  '#A39EC9', // pastel purple
  '#FF8C42', // pastel orange
  '#70D6B3', // pastel mint
  '#FF6F61', // pastel coral
  '#9F94F0', // pastel lavender
  '#FFA07A', // pastel peach
  '#FFB7B2', // pastel blush
  '#D4A5A5', // pastel rose
  '#A8D8EA', // pastel sky blue
  '#B5EAD7', // pastel seafoam
  '#C7CEEA', // pastel periwinkle
  '#FFD3B6', // pastel apricot
  '#F4B9B2', // pastel salmon
  '#E2F0CB', // pastel light lime
  '#D5C6E0', // pastel lilac
  '#E3C9C9', // pastel blush pink
];

export const isNumeric = (value: string) => /^[+-]?\d+(\.\d+)?$/.test(value);

export const formatVoiceLabel = (voiceLabel?: string, splitter = '-', joiner = ' ') => {
  const isSpeaker = voiceLabel?.includes('SPEAKER');
  if (isSpeaker) {
    return voiceLabel ? voiceLabel.split('_').join(joiner) : '';
  }
  return voiceLabel
    ? voiceLabel
        .split(splitter)
        .filter((value: string) => !isNumeric(value))
        .join(joiner)
    : '';
};

export const formatVoiceLabelToUpperCase = (label: string) => {
  return label
    .split(' ')
    .map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .join(' ');
};

export const getConfidenceColor = (confidence: number) => {
  if (confidence > 0.75) {
    return lighten(0.03, '#dbe9db');
  }
  if (confidence > 0.5) {
    return lighten(0.03, '#faf1d1');
  }
  if (confidence > 0) {
    return lighten(0.03, '#ffbeae');
  }
  return 'transparent';
};

const getDiarizationColor = (text: WpWordsType, diarizations: (DiarizationType | null)[]) => {
  const color = diarizations.find((d) => {
    return Number(d?.segment.start) <= text.start && Number(d?.segment.end) > text.end;
  })?.color;

  return color ? lighten(0.15, color) : 'transparent';
};

export const parseTranscription = (data: TranscriptionResponse[]): TranscriptionFormattedType[] => {
  const array = data
    .map((item) => {
      if (item[0].tags) {
        return {
          diarization: item[0].diarization,
          elastic_data: item[0].elastic_data,
          highlight: item[0].highlight,
          length: item[0].length,
          tags: item[0].tags,
          time: item[0].time,
          wp_words: item[0].wp_words,
        };
      }
      return null;
    })
    .filter((data) => data);
  //@ts-ignore
  return array;
};

export const parseFragmentTranscription = (
  data: TranscriptionResponse[],
): TranscriptionFormattedType[] => {
  const array = data?.map((item) => {
    return {
      diarization: item[0].diarization,
      elastic_data: item[0].elastic_data,
      highlight: item[0].highlight,
      length: item[0].length,
      tags: item[0].tags,
      time: item[0].time,
      wp_words: item[0].wp_words,
    };
  });
  return array;
};

const formatDiarization = (
  item: TranscriptionFormattedType,
  diarization: DiarizationType[],
  speakers?: { speaker: string; color: string }[],
) => {
  return Array.isArray(diarization)
    ? diarization
        .map((d) => {
          return d.label !== 'SILENCE' &&
            !(d.voice_label === 'NO IDENTIFICADO') &&
            !(d.voice_label === 'SILENCE')
            ? {
                time: toDateTimeStr(toTimestamp(item.time) + d.segment.start),
                distance: d.distance,
                label: d.label,
                segment: d.segment,
                voice_label: d.voice_label,
                color: speakers?.find((s) => s.speaker === formatVoiceLabel(d.voice_label))?.color,
              }
            : null;
        })
        .flat()
        .filter((data) => data !== null)
    : [];
};

export const formatFragmentsHover = (data: TranscriptionFormattedType[]) => {
  return data
    ? data
        .map((transcription) => {
          return transcription.wp_words.map((word) => {
            return {
              start: toTimestamp(transcription.time) + word.start * 1000,
              end: toTimestamp(transcription.time) + word.end * 1000,
              word: word.word,
            };
          });
        })
        .flat()
    : [];
};

const formatFragmentsDiarization = (
  item: TranscriptionFormattedType,
  diarization: DiarizationType,
  speakers?: { speaker: string; color: string; label: string }[],
) => {
  const diarizationData =
    diarization && diarization.label !== 'SILENCE'
      ? {
          time: item.time,
          distance: diarization.distance,
          label: diarization.label,
          segment: diarization.segment,
          words: diarization.words,
          raw: diarization.words?.map((text) => text.word).join(''),
          voice_label:
            diarization.voice_label !== 'NO IDENTIFICADO'
              ? diarization.voice_label
              : formatVoiceLabel(diarization.label),
          highlight: item.highlight,
          length: item.length ?? 0,
          tags: item.tags,
          color: speakers?.find((s) => s.label === diarization.label)?.color,
          wp_words:
            !item.tags || item.tags === 'speech' ? (
              diarization.words?.map((text, index) => {
                return (
                  <span
                    style={{
                      backgroundColor: getConfidenceColor(text.confidence),
                    }}
                    key={`text-wp-confidence-${index}`}
                  >
                    {text.word}
                  </span>
                );
              })
            ) : (
              <FormattedMessage
                id="audit.transcriptionScreen.music"
                defaultMessage="[Música/Silencio]"
              />
            ),
          original_words: item.wp_words,
        }
      : null;
  return diarizationData;
};

export const formatTranscript = (
  data: TranscriptionFormattedType[],
  speakers?: { speaker: string; color: string }[],
): FormattedTranscriptionType[] => {
  const formattedText = data?.map((item) => {
    const diarizations = item?.diarization
      ? formatDiarization(item, item?.diarization, speakers)
      : [];
    return {
      elastic_data: {
        content: item.elastic_data.content,
        customId: item.elastic_data.customId,
        diarization: diarizations,
        file: item.elastic_data.file,
        raw_content:
          !item.tags || item.tags === 'speech' ? (
            item.elastic_data.raw_content
          ) : (
            <FormattedMessage
              id="audit.transcriptionScreen.music"
              defaultMessage="[Música/Silencio]"
            />
          ),
        slug: item.elastic_data.slug,
        tags: item.elastic_data.tags,
        time: item.elastic_data.time,
        words: item.elastic_data.words,
        wp_content: item.elastic_data.wp_content,
        wp_words: item.elastic_data.wp_words,
      },
      highlight: item.highlight,
      length: item.length,
      tags: item.tags,
      time: item.time,
      wp_words:
        !item.tags || item.tags === 'speech' ? (
          item.wp_words.map((text, index) => {
            return (
              <span
                style={{ backgroundColor: getConfidenceColor(text.confidence) }}
                key={`text-wp-confidence-${index}`}
              >
                {text.word}
              </span>
            );
          })
        ) : (
          <FormattedMessage
            id="audit.transcriptionScreen.music"
            defaultMessage="[Música/Silencio]"
          />
        ),
      diarization_words:
        !item.tags || item.tags === 'speech' ? (
          item.wp_words.map((text, index) => {
            return (
              <span
                style={{
                  backgroundColor: getDiarizationColor(text, diarizations),
                }}
                key={`text-wp-diarization-${index}`}
              >
                {text.word}
              </span>
            );
          })
        ) : (
          <FormattedMessage
            id="audit.transcriptionScreen.music"
            defaultMessage="[Música/Silencio]"
          />
        ),
      original_words: item.wp_words,
    };
  });
  return formattedText;
};

export const formatFragmentsTranscript = (
  data: TranscriptionFormattedType[],
): FormattedFragmentsTranscriptionType[] => {
  let diarizations: DiarizationType[] = [];

  if (data.length > 1) {
    data.forEach((item) => {
      if (item.diarization.length) {
        diarizations.push(...item.diarization);
      }
    });
  } else {
    diarizations = data[0]?.diarization;
  }

  const speakers = getSpeakers(data);

  const formattedDiarizations =
    diarizations && diarizations[0]
      ? diarizations
          .map((d) => formatFragmentsDiarization(data[0], d, speakers))
          .flat()
          .filter((data) => data !== null)
          .map((d) => {
            return {
              ...d,
              formattedJsx: d?.words?.map((text, index) => {
                return <span key={`diarization-text-${index}`}>{text.word}</span>;
              }),
            };
          })
          .filter((data) => data.words)
      : [];

  // @ts-ignore
  return formattedDiarizations;
};

export const filteredTranscript = (row: Row<any>, raw_content: string, filterValue: string) => {
  const value = row.getValue(raw_content);
  if (row.original.tags === 'speech') {
    if (typeof value === 'string') {
      return value.toLowerCase().includes(filterValue.toLowerCase());
    } else {
      const match = (value as JSX.Element[]).find((element: JSX.Element) => {
        const word = element.props.children.toLowerCase().trim();
        return word.includes(filterValue.toLowerCase());
      });
      return !!match;
    }
  }
  return false;
};

export const getSpeakers = (data: TranscriptionType[]) => {
  let speakers: { speaker: string; color: string; label: string }[] = [];
  if (data[0]?.diarization.length) {
    data[0].diarization.forEach((diarization) => {
      if (
        diarization.voice_label !== 'SILENCE' &&
        !speakers.find((item) => {
          return item.label === diarization.label;
        })
      ) {
        speakers = [
          ...speakers,
          {
            speaker:
              diarization.voice_label === 'NO IDENTIFICADO'
                ? diarization.label.split('_').join(' ')
                : formatVoiceLabel(diarization.voice_label),
            label: diarization.label,
            color: pastelColors[speakers.length] ?? theme.aqua,
          },
        ];
      }
    });
  }
  return speakers;
};

export const formatSpeakerToSave = (speaker: string) => {
  return speaker.toLocaleLowerCase().split(' ').join('-');
};
